import ReactDOM from 'react-dom';
import React, { useEffect, useState } from 'react';
import i18n from './App/i18n';
import Main from './App/Main';
import { VisualizationProvider } from './App/ContextProviders/VisualizationProvider';
import { ConfigurationProvider } from './App/ContextProviders/ConfigurationProvider';
import { createVisualization } from './Visualization/createVisualization';
import { CustomThemeProvider } from './theme';
import { createObjectControls } from './Visualization/objectControls';
import { Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';
import Store from './App/Store';
import { LoaderStateProvider } from './App/ContextProviders/LoaderStateProvider';
import { globalLoaderStateManager } from './App/GlobalLoader';
import isDevMode from './Utils/isDevMode';
import ActivePopupProvider from './App/ContextProviders/ActivePopupProvider';
import { Frame } from './App/Frame';
import { addOptionRulesHandler } from './App/Rules/addOptionRulesHandler';
import { useLoadConfiguration } from './App/Hooks/useLoadItem';
import createTent from './App/BuildingFactory/createTent';
import createWarehouse from './App/BuildingFactory/createWarehouse';
import { useCodeSnippets } from './App/Hooks/useCodeSnippets';

const factoryFunctions = {
  tent: createTent,
  warehouse: createWarehouse,
};

const App = () => {
  const { configuration } = useLoadConfiguration();
  const [buildingConfiguration, setBuildingConfiguration] = useState(null);
  const [visualization, setVisualization] = useState(null);

  const itemIdentifier = configuration.item?.identifier;

  useCodeSnippets();

  useEffect(() => {
    if (!itemIdentifier) {
      return;
    }

    const buildingData = {
      itemIdentifier,
      ...configuration.customdata,
    };

    const createBuilding = factoryFunctions[itemIdentifier];
    if (!createBuilding) {
      console.error(`Unhandled item identifier: ${itemIdentifier}`);
      return;
    }
    const buildingConfiguration = createBuilding(buildingData, Store);
    addOptionRulesHandler(buildingConfiguration, Store);

    const visualization = createVisualization(buildingConfiguration);
    createObjectControls(visualization, buildingConfiguration);

    setBuildingConfiguration(buildingConfiguration);
    setVisualization(visualization);

    // expose objects for console hacks in dev mode
    if (isDevMode()) {
      window.visualization = visualization;
      window.configuration = configuration;
      window.buildingConfiguration = buildingConfiguration;
    }
  }, [itemIdentifier]);

  return (
    buildingConfiguration &&
    visualization && (
      <ConfigurationProvider configuration={buildingConfiguration}>
        <VisualizationProvider visualization={visualization}>
          <ActivePopupProvider>
            <Main />
          </ActivePopupProvider>
        </VisualizationProvider>
      </ConfigurationProvider>
    )
  );
};

ReactDOM.render(
  <CustomThemeProvider>
    <I18nextProvider i18n={i18n}>
      <Provider store={Store}>
        <LoaderStateProvider loaderStateManager={globalLoaderStateManager}>
          <Frame>
            <App />
          </Frame>
        </LoaderStateProvider>
      </Provider>
    </I18nextProvider>
  </CustomThemeProvider>,
  document.getElementById('root')
);
